import {window} from '@acng/frontend-bounty/dom/window.js';
import {sleep} from '@acng/frontend-bounty/sleep.js';
import {guard, STRING} from '@acng/frontend-rubicon';
import {discardToken, useToken} from '../userPool/service/token.js';
import {OBRIEN} from './service/env.js';
import {handleCfChallenge} from './service/backend/cf-challenge.js';
import {DropdownController} from '../layout/ctrl/dropdown.js';
import {ClickController} from '../layout/ctrl/click.js';
import {permission} from './permissions.js';

/**
 * Get the VAPID key for the current portal.
 *
 * Obrien contains these. Note that they have never ever changed. Therefore it
 * seems tempting to cache the VAPID. However, the current implementation would
 * not benefit by a cache.
 *
 * @returns {Promise<string>}
 */
const getVAPID = async () => {
  const token = await useToken();
  const response = await fetch(`${OBRIEN}/vapidkey`, {headers: {'x-token': token}});
  const {ok, status} = response;
  handleCfChallenge(response);

  if (!ok) {
    if (status == 401) {
      discardToken(token);
      await sleep(1000);

      return getVAPID();
    }

    throw Error('failed to get vapid key', {cause: response});
  }

  const data = await response.json();
  ASSERT: guard(data, STRING);
  return data;
};

/**
 * @param {PushSubscription} sub
 * @param {string} vapid
 *
 * @returns {Promise<void>}
 */
const postSubscription = async (sub, vapid) => {
  const token = await useToken();

  const response = await fetch(`${OBRIEN}/subscription`, {
    method: 'POST',
    body: JSON.stringify(sub),
    headers: {
      'content-type': 'application/json',
      'x-token': token,
      'x-key': vapid,
    },
  });

  const {ok, status} = response;
  handleCfChallenge(response);

  if (!ok) {
    if (status == 401) {
      discardToken(token);
      await sleep(1000);

      return postSubscription(sub, vapid);
    }

    throw response;
  }
};

/**
 * @param {PushSubscription} sub
 *
 * @returns {Promise<void>}
 */
const deleteSubscription = async (sub) => {
  const token = await useToken();

  const response = await fetch(`${OBRIEN}/subscription`, {
    method: 'DELETE',
    body: JSON.stringify(sub),
    headers: {
      'content-type': 'application/json',
      'x-token': token,
    },
  });

  const {ok, status} = response;
  handleCfChallenge(response);

  if (!ok) {
    if (status == 401) {
      discardToken(token);
      await sleep(1000);

      return deleteSubscription(sub);
    }

    throw response;
  }
};

/**
 * @param {PushManager} pushManager
 *
 * @returns {Promise<void>}
 */
const subscribe = async (pushManager) => {
  const vapid = await getVAPID();

  /**
   * Polyfill for Uint8Array.fromBase64
   *
   * @param {string} base64String
   *
   * @returns {Uint8Array}
   */
  const urlBase64ToUint8Array = (base64String) => {
    const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
    const base64 = (base64String + padding).replace(/\-/g, '+').replace(/_/g, '/');
    const rawData = window.atob(base64);

    return Uint8Array.from([...rawData].map((char) => char.charCodeAt(0)));
  };

  const sub = await pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: urlBase64ToUint8Array(vapid),
  });

  await postSubscription(sub, vapid);
};

/**
 * @param {PushManager} pushManager
 *
 * @returns {Promise<void>}
 */
const unsubscribe = async (pushManager) => {
  const sub = await pushManager.getSubscription();

  if (!sub || !(await sub.unsubscribe())) {
    throw Error('Cannot unsubscribe', {cause: sub});
  }

  await deleteSubscription(sub);
};

let done = false;

/**
 * @param {PushManager} pushManager
 */
const newPopupController = async (pushManager) => {
  const {sessionStorage} = window;
  const STORAGE_KEY = 'notification-requested';

  try {
    if (
      done ||
      sessionStorage.getItem(STORAGE_KEY) == '1' ||
      (await permission.notifications.denied()) ||
      (await pushManager.getSubscription())
    ) {
      done = true;

      return null;
    }
  } catch (reason) {
    console.error(reason);
  }

  return new DropdownController((close) => ({
    subscribe: new ClickController(() => subscribe(pushManager)),
    close: new ClickController(() => {
      try {
        sessionStorage.setItem(STORAGE_KEY, '1');
      } catch (reason) {
        console.error(reason);
      }

      done = true;
      close();
    }),
  }));
};

/**
 * @param {Promise<ServiceWorkerRegistration>} registry
 */
const newObrienController = async (registry) => {
  try {
    const {pushManager} = await registry;

    return {
      unsubscribe() {
        return unsubscribe(pushManager);
      },
      get confirm() {
        return newPopupController(pushManager);
      },
    };
  } catch (reason) {
    return null;
  }
};

export {newObrienController};
